import type { UiLocationKey } from '../components/tour/uiLocations';

/**
 * Feature encyclopedia for Documentation — what it is, what it does, how to use it.
 * Keep officer tone; use current UI names (Node Inspector, LE Report, …).
 */

export interface FeatureDocEntry {
    id: string;
    title: string;
    category: 'Workbench' | 'Investigation' | 'Cases' | 'OSINT' | 'Reports' | 'Platform';
    /** One-line meaning */
    means: string;
    /** What the feature does in the product */
    does: string;
    /** How an officer uses it (steps) */
    howTo: string[];
    /** Why it exists / when to reach for it */
    when: string;
    href?: string;
    locate?: UiLocationKey;
    related?: string[];
}

export const DOCUMENTATION_FEATURES: FeatureDocEntry[] = [
    {
        id: 'omnibar',
        title: 'Omnibar',
        category: 'Workbench',
        means: 'The single paste box for wallets, tx hashes, UPI-linked leads, and multi-seed queries.',
        does: 'Detects input type, shows a chain badge, fans out triage, and can add the subject to the canvas.',
        howTo: [
            'Open Workbench from the left sidebar.',
            'Paste an address, tx hash, or query into the Omnibar at the top.',
            'Confirm the chain badge is correct (especially 0x addresses that exist on many EVMs).',
            'Press Investigate / Add to canvas to start the graph.',
        ],
        when: 'Start of almost every live investigation — prefer Omnibar over old “Add to Canvas” patterns.',
        href: '/workbench',
        locate: 'global-omnibar',
        related: ['triage', 'canvas', 'node-inspector'],
    },
    {
        id: 'canvas',
        title: 'Graph canvas',
        category: 'Workbench',
        means: 'The Cytoscape map of wallets (nodes) and transfers (edges) for the current investigation.',
        does: 'Renders fund flow, gold trails, discovery nodes from SSE, and visibility filters. Layout is owned by the engine — algorithms declare roles, not pixel coordinates.',
        howTo: [
            'Add a subject via Omnibar or Resume a saved investigation.',
            'Click a node to open Node Inspector.',
            'Use header Trace / Links / Investigate / Analyze for the main actions.',
            'Use graph filters and eye toggles when the canvas gets noisy; Recompute Canvas after hiding many nodes.',
        ],
        when: 'Any time you need to see how money moved — not just a table of txs.',
        href: '/workbench',
        locate: 'graph-canvas',
        related: ['follow-money', 'gold-path', 'graph-filters'],
    },
    {
        id: 'node-inspector',
        title: 'Node Inspector',
        category: 'Workbench',
        means: 'The right-hand panel for the selected wallet or entity — identity, transfers, intel, and analysis.',
        does: 'Tabs: Overview, Parties, Transfers, Intel, Analysis, Actions. Identity chips summarize risk, entity, nearest exchange, escalation.',
        howTo: [
            'Click any node on the canvas.',
            'Read identity chips first (risk, entity, exchange proximity).',
            'Use Transfers for the raw activity list; Intel for sanctions/profile/exposure; Analysis for Auto-Investigate.',
        ],
        when: 'After every meaningful canvas click — this is the officer dossier surface.',
        href: '/workbench',
        locate: 'node-inspector',
        related: ['auto-investigate', 'le-report', 'intel-tab'],
    },
    {
        id: 'triage',
        title: 'Triage (identity chips)',
        category: 'Workbench',
        means: 'Fast first-look signals for the subject wallet (risk, entity, P2P, nearest exchange, labels).',
        does: 'Runs a multi-source fan-out and surfaces chips on the Inspector identity card (standalone Triage Ribbon was removed).',
        howTo: [
            'Load an address via Omnibar.',
            'Open Inspector if chips are not visible — expand Details if the identity block is collapsed.',
            'Click chips that offer “open in Intel / Analysis” to jump to the deep panel.',
        ],
        when: 'First 30 seconds of a new address — decide whether to escalate before long traces.',
        locate: 'identity-chips',
        related: ['omnibar', 'node-inspector'],
    },
    {
        id: 'follow-money',
        title: 'Follow the Money / Trace',
        category: 'Investigation',
        means: 'Highlight the fund path toward exchanges or cash-out endpoints.',
        does: 'Uses autoTrace (Neo4j / cached path query) for speed — not a full multi-minute BFS unless you run deeper investigation traces.',
        howTo: [
            'Select the subject node.',
            'Use header Trace or Analysis → Follow the Money.',
            'Read the gold trail on the canvas; open hops in Inspector as needed.',
        ],
        when: 'When you need “where did the money go?” quickly for briefing or freeze targeting.',
        locate: 'header-trace',
        related: ['gold-path', 'auto-investigate'],
    },
    {
        id: 'gold-path',
        title: 'Gold path',
        category: 'Investigation',
        means: 'The highlighted trail of edges showing the money journey Auto-Trace / Follow the Money found.',
        does: 'Visually separates the evidence path from background money-flow edges so the brief is readable.',
        howTo: [
            'Run Trace / Follow the Money.',
            'Gold edges are the story — dimmed context edges are not the claim.',
            'Export / LE Report after investigation if you need the path in the case file.',
        ],
        when: 'Court / senior briefings — point at the gold trail, not the whole hairball.',
        related: ['follow-money', 'canvas'],
    },
    {
        id: 'auto-investigate',
        title: 'Auto-Investigate (Targeted Node Intelligence)',
        category: 'Investigation',
        means: 'Sequential multi-step investigation of one subject: exchange detection → velocity → counterparties → exposure → traces → signal assessment.',
        does: 'Streams progress over SSE by default (with POST fallback). Emits live node/edge discovery onto the canvas. Ends with frontend signal assessment + AI narrative inputs for the LE Report.',
        howTo: [
            'Select the subject → header Investigate (opens Analysis tab).',
            'Run Targeted Node Intelligence and leave the tab open — steps run one at a time (required to avoid graph deadlocks).',
            'When complete, open View LE Report.',
        ],
        when: 'Full dossier on a primary suspect wallet — not every random hop.',
        locate: 'analysis-auto-investigate',
        related: ['le-report', 'node-inspector'],
    },
    {
        id: 'le-report',
        title: 'LE Report',
        category: 'Reports',
        means: 'Officer-facing printable investigation report with narrative and appendices.',
        does: 'Assembles investigation results into a brief. Interactive shell adds TOC and collapsible sections for navigation.',
        howTo: [
            'Finish Auto-Investigate (or load a saved investigation that has results).',
            'Click View LE Report in Analysis.',
            'Use the table of contents to jump; expand appendices as needed for evidence.',
        ],
        when: 'Hand-off to supervisor, FIU liaison, or case file — not a marketing PDF.',
        locate: 'le-report',
        related: ['auto-investigate'],
    },
    {
        id: 'cases',
        title: 'Cases',
        category: 'Cases',
        means: 'The case docket — FIR metadata, saved investigations, evidence, collaborators, watchlist addresses.',
        does: 'Owns the legal container for work. Workbench sessions should be bound to a case_id when the complaint has a file number.',
        howTo: [
            'Create or open a case from Cases.',
            'Use Workbench / New Investigation — choose Resume, Blank canvas, or a saved snapshot.',
            'Save snapshots from Workbench back into the case.',
        ],
        when: 'Any real complaint — keep work under a case for ownership and audit.',
        href: '/cases',
        locate: 'nav-cases',
        related: ['workbench-entry', 'le-report'],
    },
    {
        id: 'workbench-entry',
        title: 'Workbench entry choices',
        category: 'Cases',
        means: 'Explicit Resume / Blank canvas / Open saved investigation when entering Workbench from a case.',
        does: 'Prevents leftover canvas/draft from another session silently looking like “resume.” Drafts store case_id so restore cannot cross cases.',
        howTo: [
            'On Case Detail, click Workbench, New Investigation, or Add Address.',
            'Pick Resume unfinished, Blank canvas, or a saved investigation.',
            'Per-row “Resume in Workbench” on a snapshot still one-clicks.',
        ],
        when: 'Returning after lunch, OTP login, or switching cases.',
        href: '/cases',
        related: ['cases', 'canvas'],
    },
    {
        id: 'data-mode',
        title: 'Mock / Cached / Live pill',
        category: 'Platform',
        means: 'Honest label for whether the surface is demo, serving cache, or hitting live chain APIs.',
        does: 'Stops officers from briefing demo/cached data as live chain truth.',
        howTo: [
            'Look at the Workbench chrome pill while investigating.',
            'If Mock — you are in demo territory. If Cached — data may be stale. If Live — treat as production pull.',
        ],
        when: 'Always — especially before quoting numbers to a senior officer.',
        href: '/workbench',
        related: ['demos'],
    },
    {
        id: 'demos',
        title: 'Demos & Platform Tour',
        category: 'Platform',
        means: 'Guided scenarios and a tour that points at real UI controls.',
        does: 'Teaches jargon and click paths without burning live API quota. Tour + Demo Guide share one uiLocations registry.',
        howTo: [
            'Open Demos from the sidebar or Dashboard Start here.',
            'Run a scenario, then use jargon tooltips / “where is this?” pointers.',
            'Header Help → Platform Tour for the chrome walkthrough.',
        ],
        when: 'New officers, demos for partners, or practice before a live case.',
        href: '/demos',
        locate: 'dashboard-demos',
        related: ['glossary'],
    },
    {
        id: 'fusion',
        title: 'Fusion Canvas',
        category: 'OSINT',
        means: 'Join Instagram, Telegram, P2P, and bank-statement identifiers onto one case graph when they share a canonical identity (e.g. phone).',
        does: 'Uses identifier canon so +91 and 10-digit forms of the same phone merge. Workbench-style Legend toggles modalities; empty Inspector shows case overview stats. Promote from Compare IG lands on Fusion Canvas, not the /fusion sandbox. Guest Landing demos can preview Fusion without SSO.',
        howTo: [
            'Harvest leads on Instagram / Telegram / P2P desks into a case (or try a Landing guest demo first).',
            'Open Cases → your case → Fusion Canvas (not sidebar Fusion sandbox alone) to see merged identity nodes.',
            'Use Legend to show/hide Banking/Crypto/Social surfaces; read case overview in Inspector before selecting a node.',
            'Import bank statements via the shared FileDropzone; enter a PDF password when the export is encrypted.',
            'Click through to Workbench addresses when wallets appear.',
        ],
        when: 'Multi-modal cases (social + UPI + crypto + bank) — the join is the product.',
        href: '/cases',
        related: ['instagram', 'p2p', 'cases'],
    },
    {
        id: 'instagram',
        title: 'Instagram OSINT',
        category: 'OSINT',
        means: 'Desk to seed Instagram investigations from handle or phone/email/UPI identity.',
        does: 'Resolves seeds against case records / licensed providers where configured; harvests into the case graph (Fusion Canvas).',
        howTo: [
            'Open Instagram leads, select a case.',
            'Choose handle seed or phone/email/UPI seed.',
            'Promote useful identities toward Fusion Canvas / Workbench.',
        ],
        when: 'Complaints that start with a social profile or a phone, not a wallet.',
        href: '/instagram',
        locate: 'instagram-handle-input',
        related: ['fusion', 'cases'],
    },
    {
        id: 'p2p',
        title: 'P2P Intelligence',
        category: 'OSINT',
        means: 'Lookup and graphing for peer-to-peer merchant / UPI↔crypto linkage.',
        does: 'Checks addresses against P2P intelligence and surfaces merchant context for cash-out targeting.',
        howTo: [
            'Open P2P Intelligence from the sidebar.',
            'Submit addresses or follow links from triage chips.',
            'Join phones/UPI on Fusion Canvas when the case also has social harvests.',
        ],
        when: 'India cash-out legs — UPI + USDT-TRC20 desks.',
        href: '/p2p',
        related: ['fusion', 'tron'],
    },
    {
        id: 'graph-filters',
        title: 'Graph filters',
        category: 'Workbench',
        means: 'Controls to hide dust, date-bound edges, risk tiers, entity types, and hop depth.',
        does: 'Stashes filtered elements from layout so large graphs stay usable. Visibility uses `_visibility`, not CSS opacity hacks.',
        howTo: [
            'Open Filters from the Workbench header.',
            'Tighten dust / date / risk until the story is readable.',
            'Recompute Canvas after large hide passes if layout still feels wrong.',
        ],
        when: 'Hairball canvases after multi-hop expansion.',
        locate: 'header-filters',
        related: ['canvas'],
    },
    {
        id: 'intel-tab',
        title: 'Intel tab',
        category: 'Workbench',
        means: 'Sanctions, address profile, exposure, bridges, DEX swaps, approvals — auto-loaded where possible.',
        does: 'Pulls compliance and enrichment surfaces without separate “Screen OFAC” buttons from older UI.',
        howTo: [
            'Select a node → Intel tab.',
            'Read Sanctions first, then Exposure / Bridges / DEX as needed.',
            'Treat NOT TESTED vs CLEAR carefully in signal assessment language.',
        ],
        when: 'Compliance screening and multi-hop risk storytelling.',
        locate: 'inspector-tab-intel',
        related: ['node-inspector', 'auto-investigate'],
    },
];
